// noinspection JSUnusedGlobalSymbols
import { chunk, cloneDeep, escapeRegExp, groupBy, head, isArray, isNil, isNull, isPlainObject, isUndefined, keyBy, sortBy, uniq, zip, } from "lodash-es";
import { z } from "zod";
export const DEFAULT_IDENTITY_LOCALE = "en";
export const UNKNOWN_DEFAULT_IDENTITY_LOCALE = DEFAULT_IDENTITY_LOCALE;
export const DateOrISOStringSchema = z.union([z.date(), z.string()]);
export function assertNever(x, message) {
    throw new Error(message ?? "Unexpected object: " + x);
}
function throwError(errorMessage, errorConstructor) {
    throw new (errorConstructor ?? Error)(errorMessage);
}
export function decodeGlobalId(encodedGlobalId) {
    return { id: encodedGlobalId };
}
export function encodeGlobalId(_type, id) {
    return id;
}
export function stringOrThrow(str) {
    if (str === undefined || str === null)
        throw new Error("undefined or null");
    return str;
}
export const UTC_OFFSET = 120;
export function pick(base, ...keys) {
    const entries = keys.map(key => [key, base[key]]);
    return Object.fromEntries(entries);
}
export function omit(base, ...keys) {
    let obj = { ...base };
    for (let key of keys) {
        delete obj[key];
    }
    return obj;
}
export function omitDeep(collection, ...excludeKeys) {
    const clonedCollection = cloneDeep(collection);
    function omitFn(value) {
        if (value && isPlainObject(value)) {
            excludeKeys.forEach(key => {
                delete value[key];
            });
        }
    }
    function traverse(value) {
        if (isArray(value)) {
            value.forEach(traverse);
        }
        else if (value && isPlainObject(value)) {
            omitFn(value);
            Object.keys(value).forEach(key => traverse(value[key]));
        }
    }
    traverse(clonedCollection);
    return clonedCollection;
}
export function findOrThrow(arr, predicate, notFoundErrorMessage, errorConstructor) {
    const result = arr.find(predicate);
    // Guard on `undefined` exactly (Array.find's miss sentinel) via the
    // notUndefined type guard, so a found but falsy element (0, "", false) is
    // returned and the surviving value narrows to the `Exclude<T, undefined>`
    // return type. A nil-check (isNil) would wrongly reject a matched `null`.
    if (!notUndefined(result)) {
        throwError(notFoundErrorMessage ?? `not found`, errorConstructor);
    }
    return result;
}
export const MAX_LIMIT = 99999;
export function foundOrThrow(loadResult, errorMessage) {
    if (loadResult.type === "found")
        return loadResult.aggregate;
    throw new Error(errorMessage ?? "not found");
}
export function throwIfNull(
// value: (T extends Promise<infer X> ? never : T) | null
value, errorMessage, errorConstructor) {
    if (value === null) {
        throwError(errorMessage ?? `not expected null value`, errorConstructor);
    }
    return value;
}
export function throwIfNil(
// value: (T extends Promise<infer X> ? never : T) | null
value, errorMessage) {
    if (isNil(value))
        throw new Error(errorMessage ?? `not expected nil value`);
    return value;
}
export function throwIfUntruthy(value, errorMessage) {
    if (!value)
        throw new Error(errorMessage ?? `not expected nil value`);
    return value;
}
export function throwIfEmptyString(value, errorMessage, errorConstructor) {
    if (value.length === 0) {
        throwError(errorMessage ?? `not expected empty string`, errorConstructor);
    }
    return value;
}
export function throwIfUndefined(
// value: (T extends Promise<infer X> ? never : T) | null
value, errorMessage, errorConstructor) {
    if (isUndefined(value)) {
        throwError(errorMessage ?? `not found`, errorConstructor);
    }
    return value;
}
export function throwIfAnyNull(
// value: (T extends Promise<infer X> ? never : T) | null
values, errorMessage, errorConstructor) {
    const nonNullValues = filterNonNull(values);
    if (values.length !== nonNullValues.length) {
        throwError(errorMessage ?? `not expected null value in "${values}"`, errorConstructor);
    }
    return nonNullValues;
}
export function headOrThrow(values, errorMessage, errorConstructor) {
    return throwIfUndefined(head(values), errorMessage, errorConstructor);
}
export function valuesByKeys(values, keys, valueKey) {
    const valuesMap = keyBy(values, value => valueKey(value));
    return keys.map(key => valuesMap[key] ?? null);
}
export function groupedValuesByKeys(values, keys, valueKey) {
    const valuesMap = groupBy(values, value => valueKey(value));
    return keys.map(key => valuesMap[key] ?? []);
}
export function sortedGroupedValuesByKeys(values, keys, valueKey, iteratee // FIXME
) {
    const valuesMap = groupBy(values, value => valueKey(value));
    return keys.map(key => sortBy(valuesMap[key], iteratee) ?? []);
}
export function isPicture(mimeType) {
    return mimeType.toLowerCase().startsWith("image");
}
export function getPictures(attachments) {
    return attachments.filter(e => isPicture(e.mimeType));
}
export function getFiles(attachments) {
    return attachments.filter(e => !isPicture(e.mimeType));
}
export function getNameWithCopy(name, copySuffix) {
    // const regexp = new RegExp(`^(.*) - ${copySuffix}( [0-9]+)?$`, "gi");
    // const [, originalName] = name.match(regexp) ?? [];
    // return `${originalName ?? name} - ${copySuffix}`;
    return `${name} - ${copySuffix}`;
}
// export type DeepReadonly<T> = {
//   readonly [P in keyof T]: DeepReadonly<T[P]>;
// };
export function wrapErrorWithContext(cause, context) {
    const message = [
        ...(cause?.message?.split(";") ?? []),
        ...Object.entries(context).map(([key, val]) => `${key}: ${val}`),
    ].join(";");
    throw new Error(message, { cause });
}
export function enhanceWithFullName(subject) {
    return {
        ...subject,
        fullname: `${subject.firstname} ${subject.familyname}`.trim(),
    };
}
export function notNull(value) {
    return value !== null;
}
export function notUndefined(value) {
    return value !== undefined;
}
export function notNullNorUndefined(value) {
    return value !== null && value !== undefined;
}
export function requiredValue(value, valueOnUndefined) {
    return isUndefined(value) ? valueOnUndefined : value;
}
export function nonNilValue(value, valueOnUndefined, valueOnNull) {
    return isUndefined(value)
        ? valueOnUndefined
        : isNull(value)
            ? valueOnNull
            : value;
}
export const parseNumber = (number, prefix, minLength) => {
    // Escape the prefix BEFORE substituting the date placeholders: escapeRegExp
    // leaves `<`/`>` untouched, so the `<YYYY>`/`<YY>`/`<MM>`/`<DD>` tokens survive
    // to be expanded here, while genuine metacharacters in the literal prefix are
    // matched literally rather than acting as regex syntax.
    const regexp = new RegExp(`^${escapeRegExp(prefix)
        .replace(/<YYYY>/gi, "[0-9]{4}")
        .replace(/<YY>/gi, "[0-9]{2}")
        .replace(/<MM>/gi, "[0-9]{2}")
        .replace(/<DD>/gi, "[0-9]{2}")}([0-9]+)$`);
    const [, counter] = number.match(regexp) ?? [];
    if (!counter)
        throw new Error(`Number ${number} does not match provided prefix: ${prefix}`);
    if (counter.length < minLength)
        throw new Error(`Counter ${counter} length is smaller than min length: ${minLength}`);
    return parseInt(counter, 10);
};
export async function wait(ms) {
    return new Promise(resolve => {
        setTimeout(resolve, ms);
    });
}
export function zipOrThrow(array1, array2) {
    if (array1.length !== array2.length) {
        throw new Error(`Arrays have different lengths ${array1.length} and ${array2.length}`);
    }
    return zip(array1, array2);
}
export function zipOrThrow3(array1, array2, array3) {
    if (uniq([array1.length, array2.length, array3.length]).length !== 1) {
        throw new Error(`Arrays have different lengths ${array1.length} and ${array2.length} and ${array3.length}`);
    }
    return zip(array1, array2, array3);
}
export function filterNonNull(array) {
    return array.filter(notNull);
}
/**
 * Batches an array through `mapValueFn`, runs a single bulk `fn` over the kept
 * values, then realigns results to the original positions.
 *
 * `mapValueFn` returning `null` (and only `null`) skips that position — the
 * corresponding output is `null`. Falsy-but-non-null mapped values (`0`, `""`,
 * `false`, `NaN`) are kept and passed to `fn`.
 *
 * `fn` MUST return exactly one result per non-null mapped value, in order,
 * otherwise the internal `zipOrThrow` throws `Arrays have different lengths`.
 */
export async function doBy(array, mapValueFn, fn) {
    const valuesMapped = filterNonNull(array.map((value, index) => {
        const mapped = mapValueFn(value);
        return mapped !== null ? { value: mapped, index } : null;
    }));
    const results = await fn(valuesMapped.map(e => e.value));
    return valuesByKeys(zipOrThrow(results, valuesMapped).map(([result, { index }]) => ({
        result,
        index,
    })), array.map((_, index) => index), e => e.index).map(e => (e ? e.result : null));
}
/**
 * Synchronous counterpart of {@link doBy}: batches an array through
 * `mapValueFn`, runs a single bulk `fn` over the kept values, then realigns
 * results to the original positions.
 *
 * `mapValueFn` returning `null` (and only `null`) skips that position — the
 * corresponding output is `null`. Falsy-but-non-null mapped values (`0`, `""`,
 * `false`, `NaN`) are kept and passed to `fn`.
 *
 * `fn` MUST return exactly one result per non-null mapped value, in order,
 * otherwise the internal `zipOrThrow` throws `Arrays have different lengths`.
 */
export function doBySync(array, mapValueFn, fn) {
    const valuesMapped = filterNonNull(array.map((value, index) => {
        const mapped = mapValueFn(value);
        return mapped !== null ? { value: mapped, index } : null;
    }));
    const results = fn(valuesMapped.map(e => e.value));
    return valuesByKeys(zipOrThrow(results, valuesMapped).map(([result, { index }]) => ({
        result,
        index,
    })), array.map((_, index) => index), e => e.index).map(e => (e ? e.result : null));
}
export function filterNonNil(array) {
    return array.filter(notNullNorUndefined);
}
/**
 * Assume a float with country specific thousand grouper and country specific decimal, removes the grouper and replaces the decimal with "."
 */
export function parseCountryFloat(value, opts) {
    let cleanedValue = value;
    if (opts.grouper) {
        cleanedValue = cleanedValue.replace(new RegExp(escapeRegExp(opts.grouper), "g"), "");
    }
    cleanedValue = cleanedValue.replace(new RegExp(escapeRegExp(opts.decimal), "g"), ".");
    const parsedValue = parseFloat(cleanedValue);
    if (Number.isNaN(parsedValue) || !Number.isFinite(parsedValue)) {
        throw new Error(`Not a valid number "${parsedValue}" from "${value}"`);
    }
    return parsedValue;
}
export class ConcurrentDocumentModificationError extends Error {
}
export function getLocaleLanguageAndCountry(locale) {
    const [language, country] = locale.split("-");
    return { language, country };
}
export function getFilenameFromContentDisposition(contentDisposition = "") {
    const extended = /filename\*=[^']*'[^']*'([^;]*)/.exec(contentDisposition);
    if (extended) {
        try {
            return decodeURIComponent(extended[1].trim());
        }
        catch {
            // not UTF-8 percent-encoding, use the plain filename= instead
        }
    }
    return /filename=([^;]*)/.exec(contentDisposition)?.[1].replaceAll('"', "");
}
export function* arrayToChunkIterator(array, chunkSize) {
    for (const c of chunk(array, chunkSize)) {
        yield c;
    }
}
function isNotError(value) {
    return (value.__typename !== "MissingCapabilities" &&
        value.__typename !== "MissingPermissions");
}
export function getDataOrNull(value) {
    if (value === undefined || value === null)
        return null;
    return isNotError(value) ? value : null;
}
export function getDataOrThrow(value) {
    if (!isNotError(value))
        throw new Error(value.__typename);
    return value;
}
export function dateOrIsoString2DateIsoString(value) {
    return typeof value === "string" ? value : value.toISOString();
}
/**
 * Returns the element if the array has exactly one item, otherwise null.
 * Useful for resolving a single value from an optional array field
 * (e.g. template project types: 1 type → use it, 0 or many → null).
 */
export function resolveSingleArrayElement(arr) {
    if (arr?.length === 1)
        return arr[0];
    return null;
}
//# sourceMappingURL=common.js.map